// ── AuditCard ─────────────────────────────────────────────────────────────────
// Shows ADA/SEO audit results: score, violation summary, auto-fixes applied,
// and a collapsible list of violations.
// Placed in the Design tab, below page preview or delivered view.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { parseServerDate } from '../../lib/date.js';
import {
  useProjectAudits,
  useRunAudit,
  type AuditListItem,
  type AuditSummary,
} from '../../hooks/useProjects.js';

// ── Score ring ────────────────────────────────────────────────────────────────

function ScoreRing({ score }: { score: number | null }) {
  const s = score ?? 0;
  const color = s >= 80 ? 'var(--evo-green, #22c55e)' : s >= 50 ? 'var(--evo-amber)' : 'var(--evo-danger)';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 'var(--evo-space-1)',
      flexShrink: 0,
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        border: `3px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--evo-font)',
        fontSize: 'var(--evo-fs-title)',
        fontWeight: 'var(--evo-fw-bold)',
        color,
      }}>
        {score === null ? '—' : s}
      </div>
      <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
        Score
      </span>
    </div>
  );
}

// ── Severity pill ─────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'var(--evo-danger)',
  serious:  'var(--evo-amber)',
  moderate: 'var(--evo-cyan)',
  minor:    'var(--evo-fg-3)',
};

function SeverityPill({ label, count }: { label: string; count: number }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
      minWidth: '48px',
    }}>
      <span style={{
        fontSize: 'var(--evo-fs-title)',
        fontWeight: 'var(--evo-fw-bold)',
        fontFamily: 'var(--evo-font)',
        color: count > 0 ? SEVERITY_COLOR[label] : 'var(--evo-fg-4)',
        lineHeight: 1,
      }}>
        {count}
      </span>
      <span style={{
        fontSize: '10px',
        fontFamily: 'var(--evo-font)',
        color: 'var(--evo-fg-3)',
        textTransform: 'capitalize',
      }}>
        {label}
      </span>
    </div>
  );
}

// ── Audit result card (single audit row expanded) ─────────────────────────────

function AuditResultCard({ audit }: { audit: AuditListItem }) {
  const summary = audit.summary as AuditSummary;
  const date = parseServerDate(audit.auditedAt).toLocaleDateString('es', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  const label = audit.targetType === 'revision' ? `R${audit.targetVersion}` : `v${audit.targetVersion}`;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--evo-space-4)',
      padding: 'var(--evo-space-3) var(--evo-space-4)',
      background: 'var(--evo-bg-2)',
      borderRadius: 'var(--evo-r)',
      border: '1px solid var(--evo-stroke)',
    }}>
      <ScoreRing score={audit.score} />

      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
          marginBottom: 'var(--evo-space-2)',
        }}>
          <span style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', fontWeight: 'var(--evo-fw-medium)' }}>
            {label}
          </span>
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)' }}>
            {date}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 'var(--evo-space-4)' }}>
          <SeverityPill label="critical" count={summary.critical} />
          <SeverityPill label="serious"  count={summary.serious}  />
          <SeverityPill label="moderate" count={summary.moderate} />
          <SeverityPill label="minor"    count={summary.minor}    />
        </div>
      </div>
    </div>
  );
}

// ── Main AuditCard ────────────────────────────────────────────────────────────

interface Props {
  projectId: number;
}

export function AuditCard({ projectId }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: audits, isLoading: auditsLoading } = useProjectAudits(projectId);
  const runAudit = useRunAudit(projectId);

  const latest = audits?.[0];
  const hasAudits = (audits?.length ?? 0) > 0;

  function handleRunAudit() {
    runAudit.mutate();
  }

  return (
    <div style={{
      borderTop: '1px solid var(--evo-stroke)',
      padding: 'var(--evo-space-4) var(--evo-content-pad-x)',
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: hasAudits ? 'var(--evo-space-3)' : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex' }}>
            <Icon name="bolt" size={16} />
          </span>
          <span style={{
            fontSize: 'var(--evo-fs-label)',
            fontFamily: 'var(--evo-font)',
            fontWeight: 'var(--evo-fw-medium)',
            color: 'var(--evo-fg-2)',
          }}>
            {t('projects.audit.title', 'Reporte de calidad ADA/SEO')}
          </span>
          {auditsLoading && (
            <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
              {t('common.loading', 'Cargando…')}
            </span>
          )}
        </div>

        <Button
          variant="outline"
          tone="muted"
          size="sm"
          icon={<Icon name="sparkle" size={12} />}
          disabled={runAudit.isPending}
          loading={runAudit.isPending}
          onClick={handleRunAudit}
        >
          {runAudit.isPending
            ? t('projects.audit.running', 'Auditando…')
            : hasAudits
              ? t('projects.audit.rerun', 'Re-auditar')
              : t('projects.audit.run', 'Generar reporte de calidad')}
        </Button>
      </div>

      {/* Error state */}
      {runAudit.isError && (
        <p style={{
          fontSize: 'var(--evo-fs-caption)',
          color: 'var(--evo-danger)',
          fontFamily: 'var(--evo-font)',
          margin: 'var(--evo-space-2) 0 0',
        }}>
          {runAudit.error instanceof Error ? runAudit.error.message : t('common.error', 'Error al auditar')}
        </p>
      )}

      {/* Latest audit result */}
      {latest && <AuditResultCard audit={latest} />}

      {/* History toggle */}
      {(audits?.length ?? 0) > 1 && (
        <div style={{ marginTop: 'var(--evo-space-2)' }}>
          <Button
            variant="link"
            tone="muted"
            size="sm"
            icon={<span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }}><Icon name="chevron-down" size={12} /></span>}
            onClick={() => setOpen(o => !o)}
          >
            {open
              ? t('projects.audit.hideHistory', 'Ocultar historial')
              : t('projects.audit.showHistory', `Ver historial (${audits!.length - 1} más)`)}
          </Button>
        </div>
      )}

      {/* Historical audits */}
      {open && audits && audits.slice(1).map(a => (
        <div key={a.id} style={{ marginTop: 'var(--evo-space-2)' }}>
          <AuditResultCard audit={a} />
        </div>
      ))}
    </div>
  );
}
